<template>
  <div class="menu-profile">
    <div class="menu-body">
      <table class="table-property">
        <tbody>
          <tr v-if="myself || user.meta.isShareEmail">
            <th>{{ $t('models.user.attributes.emailShort') }}</th>
            <td>
              <a :href="`mailto:${user.email}`" class="text-link">{{ user.email }}</a>
              <span v-if="!user.meta.isShareEmail" class="ml-1">({{ $t('common.hide') }})</span>
            </td>
          </tr>
          <tr v-if="myself || user.meta.isSharePhone">
            <th>{{ $t('models.user.attributes.phone') }}</th>
            <td>
              <a :href="`tel:${user.meta.phone}`" class="text-link">{{ user.meta.phone }}</a>
              <span v-if="user.meta.phone && !user.meta.isSharePhone" class="ml-1">({{ $t('common.hide') }})</span>
            </td>
          </tr>
          <tr>
            <th valign="top">{{ $t('models.user.attributes.publicDescription') }}</th>
            <td class="text-pre-wrap" valign="top">{{ user.meta.publicDescription }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-if="myself && editUser" class="menu-footer">
      <proa-button @click.native="editMyProfileModal = true">
        {{ $t('common.editProfile') }}
      </proa-button>
      <proa-modal
        :modal="editMyProfileModal"
        :title="$t('common.modals.editMyProfileModal.title')"
        @close="editMyProfileModal = false"
      >
        <div class="edit-avatar-wrapper">
          <proa-avatar
            :src="removeAvatar ? require('@/assets/images/avatar_default.png') : uploadAvatarData || user.meta.avatar || require('@/assets/images/avatar_default.png')"
            class="mr-3"
          />
          <div class="d-flex flex-column justify-content-around">
            <span class="title d-block mb-2 mb-md-0">{{ $t('models.user.attributes.avatar') }}</span>
            <div class="d-flex flex-wrap align-items-center">
              <proa-button :disabled="editMyProfileDisabled" @click.native="onClickUploadAvatar">
                {{ $t('common.modals.editMyProfileModal.uploadPhoto') }}
              </proa-button>
              <div :class="['avatar-remove mt-2 mt-md-0', { 'disabled': editMyProfileDisabled }]" @click="onClickRemoveAvatar">
                <span class="avatar-remove-icon">
                  <img :src="require('@/assets/images/icons/cross.png')"/>
                </span>
                <span class="avatar-remove-caption">
                  {{ $t('common.modals.editMyProfileModal.deletePhoto') }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <proa-input-group :label="$t('models.user.attributes.displayName')">
          <template slot="input">
            <proa-input
              v-model="editUser.userPropertyAttributes.displayName"
              :disabled="editMyProfileDisabled"
              type="text"
              dark
            />
          </template>
        </proa-input-group>
        <proa-input-group :label="$t('models.user.attributes.phone')">
          <template slot="input">
            <proa-input
              v-model="editUser.userPropertyAttributes.phone"
              :disabled="editMyProfileDisabled"
              type="text"
              dark
            />
          </template>
        </proa-input-group>
        <proa-input-group :label="$t('models.user.attributes.publicDescription')">
          <template slot="input">
            <proa-input
              v-model="editUser.userPropertyAttributes.publicDescription"
              :disabled="editMyProfileDisabled"
              type="textarea"
              rows="5"
              dark
            />
          </template>
        </proa-input-group>
        <template slot="footer">
          <proa-button
            :disabled="editMyProfileDisabled"
            @click.native="editMyProfileModal = false"
            class="mr-2"
          >
            {{ $t('common.cancel') }}
          </proa-button>
          <proa-button
            :loading="editMyProfileDisabled"
            :disabled="!editUser.userPropertyAttributes.displayName"
            @click.native="$emit('onClickUpdateUser', editUser)"
            variant="primary"
          >
            {{ $t('common.change') }}
          </proa-button>
        </template>
      </proa-modal>
    </div>
  </div>
</template>

<script>
import User from '@/store/models/User'
import VirtualInputFile from '@/lib/VirtualInputFile'

export default {
  name: 'MemberProfileTabMenuProfile',
  props: {
    myself: {
      type: Boolean,
      default: false
    },
    user: {
      type: Object,
      default: () => {}
    },
  },
  data() {
    return {
      editMyProfileDisabled: false,
      editMyProfileModal: false,
      editUser: null,
      removeAvatar: false,
      uploadAvatarData: null
    }
  },
  methods: {
    onClickUploadAvatar: function() {
      const virtualInputFile = new VirtualInputFile({
        accept: 'image/*',
        limit: false
      })

      virtualInputFile.on('fileRead', (file) => {
        if (!file.type.startsWith('image')) return
        this.removeAvatar = this.editUser.userPropertyAttributes.removeAvatar = false
        this.uploadAvatarData = this.editUser.userPropertyAttributes.avatar = file.base64Data
      })
    },
    onClickRemoveAvatar: function() {
      if (this.editMyProfileDisabled) return
      this.removeAvatar = this.editUser.userPropertyAttributes.removeAvatar = true
      this.uploadAvatarData = this.editUser.userPropertyAttributes.avatar = null
    },
    setEditUserAttributes: function() {
      this.uploadAvatarData = null
      this.removeAvatar = false
      this.editUser = Object.assign(User.skeleton(), {
        email: this.user.email,
        firstName: this.user.firstName,
        lastName: this.user.lastName,
        userPropertyAttributes: {
          displayName: this.user.meta.displayName,
          phone: this.user.meta.phone,
          publicDescription: this.user.meta.publicDescription
        }
      })
    }
  },
  mounted() {
    if (this.myself) this.setEditUserAttributes()
  },
  watch: {
    editMyProfileModal: function(value) {
      if (!value) this.setEditUserAttributes()
    },
    myself: function(value) {
      if (value) this.setEditUserAttributes()
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/styles/variables";
@import "@/styles/mediaquery";

.menu-profile {
  height: 100%;
  display: flex;
  flex-direction: column;

  .menu-body {
    flex-grow: 1;
    padding: 20px;

    .table-property {
      margin-bottom: 25px;

      th, td {
        padding: 0 15px 10px 0;
        font-size: 15px;

        @include mq(md) {
          padding: 0 17px 15px 0;
          font-size: 14px;
        }
      }

      th {
        font-weight: 100;
        color: $secondary-color;
        min-width: 75px;

        @include mq(md) {
          font-weight: 600;
          color: #000;
          min-width: 73px;
        }
      }
    }
  }

  .menu-footer {
    display: flex;
    justify-content: center;
    padding: 20px;
    border-top: 1px solid $dark-border-color;

    @include mq(md) {
      justify-content: flex-end;
    }
  }
}

.edit-avatar-wrapper {
  margin-bottom: 20px;
  display: flex;
  align-items: stretch;

  .title {
    font-size: 14px;
    font-weight: 600;
    color: $secondary-color;
  }

  .avatar-remove {
    display: flex;

    &:not(.disabled) {
      cursor: pointer;

      &:hover .avatar-remove-icon {
        background-color: $light-border-color;
      }
    }

    .avatar-remove-icon {
      margin-left: 30px;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background-color: $background-color;
      display: flex;
      align-items: center;
      justify-content: center;

      img {
        width: 8px;
        height: 8px;
      }
    }

    .avatar-remove-caption {
      margin-left: 10px;
      font-size: 13px;
      color: $light-text-color;
      user-select: none;
    }
  }
}
</style>
